/**
 * Nuevos Estudios · Configuracion -> Estudios -> Nuevos Estudios.
 *
 * Izquierda: formulario para crear/editar la informacion de proximos estudios.
 * Derecha: lista de estudios con switch "Publicar", editar y borrar.
 * Solo los publicados se exponen a la web y a la app de voluntarios.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { FloppyDisk, Trash, PencilSimple, Plus, X, Flask, Users, Phone, Envelope, CheckCircle, Warning, ArrowClockwise, MagnifyingGlass, LinkSimple } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '../ui/command';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EP = `${API_URL}/api/config/upcoming-studies`;

const EMPTY = {
  name: '', executive_summary: '', inclusion_criteria: '', exclusion_criteria: '', withdrawal_criteria: '',
  duration_visits: '', compensation: '', visit_times: '', source_study_id: '',
  audience_type: 'all', audience_segment_id: '',
};

export default function UpcomingStudiesPanel({ lang }) {
  const es = lang === 'es';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [interestsFor, setInterestsFor] = useState(null);
  const [interestRows, setInterestRows] = useState([]);
  const [interestsLoading, setInterestsLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [resendingWebId, setResendingWebId] = useState(null);
  // Autocarga desde estudio existente
  const [sourceStudy, setSourceStudy] = useState(null);
  const [studyOpen, setStudyOpen] = useState(false);
  const [studyQuery, setStudyQuery] = useState('');
  const [studyResults, setStudyResults] = useState([]);
  const [studySearching, setStudySearching] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [segments, setSegments] = useState([]);
  const [segmentCounts, setSegmentCounts] = useState({});

  const L = {
    title: es ? 'Nuevos Estudios' : 'Upcoming Studies',
    intro: es
      ? 'Registra la informacion de los proximos estudios. Marca "Publicar" para mostrarlos en la web y en la app de voluntarios.'
      : 'Register upcoming studies. Toggle "Publish" to show them on the web and the volunteer app.',
    name: es ? 'Nombre de Estudio' : 'Study name',
    executiveSummary: es ? 'Resumen ejecutivo' : 'Executive summary',
    inclusion: es ? 'Criterios de Inclusion' : 'Inclusion criteria',
    exclusion: es ? 'Criterios de Exclusion' : 'Exclusion criteria',
    withdrawal: es ? 'Criterios de Retirada' : 'Withdrawal criteria',
    duration: es ? 'Duracion / Visitas' : 'Duration / Visits',
    compensation: es ? 'Remuneracion' : 'Compensation',
    visit_times: es ? 'Tiempos de las visitas' : 'Visit times',
    audience: es ? 'Quien lo ve en la App' : 'Who sees it in the App',
    audienceHint: es ? 'En la web publica siempre se muestra a todos. Esto solo afecta a la App de voluntarios.' : 'On the public web it is always shown to everyone. This only affects the volunteer App.',
    audienceAll: es ? 'Todos los voluntarios' : 'All volunteers',
    audienceSegment: es ? 'Solo un segmento' : 'Only a segment',
    audiencePickSegment: es ? 'Elige un segmento…' : 'Pick a segment…',
    audienceNoSegments: es ? 'No hay segmentos. Creálos en Reclutamiento.' : 'No segments. Create them in Recruitment.',
    optional: es ? '(opcional)' : '(optional)',
    create: es ? 'Crear estudio' : 'Create study',
    update: es ? 'Guardar cambios' : 'Save changes',
    cancel: es ? 'Cancelar' : 'Cancel',
    new: es ? 'Nuevo' : 'New',
    list: es ? 'Estudios registrados' : 'Registered studies',
    empty: es ? 'Aun no hay estudios registrados.' : 'No studies yet.',
    publish: es ? 'Publicar' : 'Publish',
    published: es ? 'Publicado' : 'Published',
    draft: es ? 'Borrador' : 'Draft',
    webSynced: es ? 'En web' : 'On web',
    webPending: es ? 'Web pendiente' : 'Web pending',
    webError: es ? 'Web error' : 'Web error',
    webResend: es ? 'Reenviar a la web' : 'Resend to web',
    webResent: es ? 'Estudio enviado a la web' : 'Study sent to web',
    edit: es ? 'Editar' : 'Edit',
    del: es ? 'Eliminar' : 'Delete',
    confirmDel: es ? '¿Eliminar este estudio?' : 'Delete this study?',
    created: es ? 'Estudio creado' : 'Study created',
    updated: es ? 'Estudio actualizado' : 'Study updated',
    deleted: es ? 'Estudio eliminado' : 'Study deleted',
    interested: es ? 'interesados' : 'interested',
    interestedTitle: es ? 'Voluntarios interesados' : 'Interested volunteers',
    noInterest: es ? 'Nadie se ha apuntado todavia.' : 'No one has signed up yet.',
    date: es ? 'Fecha' : 'Date',
    crmSynced: es ? 'En CRM' : 'In CRM',
    crmPending: es ? 'CRM pendiente' : 'CRM pending',
    crmError: es ? 'CRM error' : 'CRM error',
    crmResend: es ? 'Reenviar al CRM' : 'Resend to CRM',
    crmResent: es ? 'Lead enviado al CRM' : 'Lead sent to CRM',
    loadFromStudy: es ? 'Cargar desde un estudio existente' : 'Load from an existing study',
    loadFromStudyHint: es
      ? 'Busca un estudio por su título y autocompleta los campos desde su Protocolo. Luego puedes editarlos. (Opcional)'
      : 'Search a study by title and autofill fields from its Protocol. You can edit them afterwards. (Optional)',
    searchStudy: es ? 'Buscar estudio por título…' : 'Search study by title…',
    typeToSearch: es ? 'Escribe al menos 2 letras para buscar…' : 'Type at least 2 letters to search…',
    noStudies: es ? 'No se encontraron estudios.' : 'No studies found.',
    linkedTo: es ? 'Cargado desde' : 'Loaded from',
    loaded: es ? 'Campos cargados desde el estudio' : 'Fields loaded from study',
    clearLink: es ? 'Quitar vínculo' : 'Clear link',
  };

  // Busqueda de estudios (debounce) para el selector de autocarga.
  useEffect(() => {
    const q = (studyQuery || '').trim();
    if (q.length < 2) { setStudyResults([]); return; }
    let active = true;
    setStudySearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/studies`, {
          params: { q, paginated: 'true', limit: 10 }, withCredentials: true,
        });
        if (active) setStudyResults(r.data?.data || []);
      } catch (_) { if (active) setStudyResults([]); }
      finally { if (active) setStudySearching(false); }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [studyQuery]);

  const selectStudy = async (s) => {
    setStudyOpen(false);
    setSourceStudy({ study_id: s.study_id, code: s.code, title: s.title });
    setPrefilling(true);
    try {
      const r = await axios.get(`${EP}/prefill/${s.study_id}`, { withCredentials: true });
      const d = r.data || {};
      setForm((f) => ({
        ...f,
        name: d.name || '',
        executive_summary: d.executive_summary || '',
        inclusion_criteria: d.inclusion_criteria || '',
        exclusion_criteria: d.exclusion_criteria || '',
        withdrawal_criteria: d.withdrawal_criteria || '',
        duration_visits: d.duration_visits || '',
        compensation: d.compensation || '',
        source_study_id: d.source_study_id || s.study_id,
        // visit_times se mantiene manual (no se toca)
      }));
      toast.success(L.loaded);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setPrefilling(false);
    }
  };

  const clearSource = () => {
    setSourceStudy(null);
    setForm((f) => ({ ...f, source_study_id: '' }));
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(EP, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/recruitment/segments`, { withCredentials: true })
      .then((r) => {
        const segs = r.data || [];
        setSegments(segs);
        // Contador de voluntarios por segmento (recuento dinamico, en paralelo).
        segs.forEach((s) => {
          axios.get(`${API_URL}/api/recruitment/segments/${s.id}/count`, { withCredentials: true })
            .then((cr) => setSegmentCounts((prev) => ({ ...prev, [s.id]: cr.data?.total ?? null })))
            .catch(() => { /* ignora: queda sin contador */ });
        });
      })
      .catch(() => { /* sin permiso de segmentos: queda vacio, solo "Todos" */ });
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const resetForm = () => { setForm(EMPTY); setEditingId(null); setSourceStudy(null); setStudyQuery(''); setStudyResults([]); };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      name: it.name || '', executive_summary: it.executive_summary || '', inclusion_criteria: it.inclusion_criteria || '',
      exclusion_criteria: it.exclusion_criteria || '', withdrawal_criteria: it.withdrawal_criteria || '',
      duration_visits: it.duration_visits || '', compensation: it.compensation || '',
      visit_times: it.visit_times || '', source_study_id: it.source_study_id || '',
      audience_type: it.audience_type === 'segment' ? 'segment' : 'all',
      audience_segment_id: it.audience_segment_id || '',
    });
    setSourceStudy(it.source_study_id ? { study_id: it.source_study_id, title: it.name, code: '' } : null);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
  };

  const requiredOk = () =>
    ['name', 'executive_summary', 'inclusion_criteria', 'exclusion_criteria', 'duration_visits', 'compensation', 'visit_times']
      .every((k) => (form[k] || '').trim());

  const save = async () => {
    if (!requiredOk()) { toast.error(es ? 'Completa los campos obligatorios' : 'Fill the required fields'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${EP}/${editingId}`, form, { withCredentials: true });
        toast.success(L.updated);
      } else {
        await axios.post(EP, form, { withCredentials: true });
        toast.success(L.created);
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (it) => {
    try {
      await axios.patch(`${EP}/${it.id}/publish`, { published: !it.published }, { withCredentials: true });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, published: !it.published } : x)));
      // El envio a la web es en 2o plano; recargamos tras un instante para reflejar el estado de sync.
      setTimeout(() => { load(); }, 1500);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const resendWeb = async (it) => {
    setResendingWebId(it.id);
    try {
      const r = await axios.post(`${EP}/${it.id}/resend-web`, {}, { withCredentials: true });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, ...r.data } : x)));
      if (r.data?.ok) toast.success(L.webResent);
      else toast.error(r.data?.web_sync_error || 'Web error');
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setResendingWebId(null);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(L.confirmDel)) return;
    try {
      await axios.delete(`${EP}/${it.id}`, { withCredentials: true });
      toast.success(L.deleted);
      if (editingId === it.id) resetForm();
      await load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const openInterests = async (it) => {
    setInterestsFor(it);
    setInterestsLoading(true);
    setInterestRows([]);
    try {
      const r = await axios.get(`${EP}/${it.id}/interests`, { withCredentials: true });
      setInterestRows(r.data || []);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setInterestsLoading(false);
    }
  };

  const resendCrm = async (row) => {
    setResendingId(row.id);
    try {
      const r = await axios.post(`${EP}/interests/${row.id}/resend-crm`, {}, { withCredentials: true });
      setInterestRows(prev => prev.map(x => x.id === row.id ? { ...x, ...r.data } : x));
      if (r.data?.ok) toast.success(L.crmResent);
      else toast.error(r.data?.crm_error || 'CRM error');
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setResendingId(null);
    }
  };

  const fieldText = (label, key, { optional = false, rows = 3, input = false } = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`us-${key}`} className="text-sm font-medium text-stone-700">
        {label}{optional ? <span className="text-stone-400 font-normal"> {L.optional}</span> : <span className="text-red-500"> *</span>}
      </Label>
      {input ? (
        <Input id={`us-${key}`} data-testid={`upcoming-${key}`} value={form[key]} onChange={set(key)} className="rounded-sm" />
      ) : (
        <Textarea id={`us-${key}`} data-testid={`upcoming-${key}`} value={form[key]} onChange={set(key)} rows={rows} className="rounded-sm resize-y" />
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="upcoming-studies-panel">
      {/* Formulario */}
      <div className="rounded-sm border border-stone-200 bg-white p-5 space-y-4">
        <div className="flex items-center gap-2 border-b border-stone-100 pb-3">
          <Flask size={20} className="text-[#1f3e80]" />
          <h3 className="text-lg font-heading font-semibold text-stone-900">
            {editingId ? L.edit : L.new}
          </h3>
          {editingId && (
            <Button variant="ghost" size="sm" onClick={resetForm} data-testid="upcoming-cancel-edit" className="ml-auto text-stone-500 gap-1 h-7">
              <X size={14} />{L.cancel}
            </Button>
          )}
        </div>
        <p className="text-xs text-stone-500">{L.intro}</p>

        {/* Autocarga desde un estudio existente (opcional) */}
        <div className="rounded-sm border border-dashed border-[#1f3e80]/30 bg-[#1f3e80]/[0.03] p-3 space-y-2" data-testid="upcoming-load-from-study">
          <Label className="text-sm font-medium text-[#1f3e80] flex items-center gap-1.5">
            <LinkSimple size={15} /> {L.loadFromStudy}
          </Label>
          <p className="text-xs text-stone-500">{L.loadFromStudyHint}</p>
          {sourceStudy ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-[#1f3e80]/40 text-[#1f3e80] bg-white gap-1 max-w-full">
                <LinkSimple size={12} />
                <span className="truncate">{L.linkedTo}: {sourceStudy.code ? `${sourceStudy.code} · ` : ''}{sourceStudy.title || sourceStudy.study_id}</span>
              </Badge>
              <Button type="button" variant="ghost" size="sm" onClick={clearSource}
                data-testid="upcoming-clear-source" className="h-6 px-2 text-[11px] text-stone-500 gap-1">
                <X size={12} /> {L.clearLink}
              </Button>
              {prefilling && <ArrowClockwise size={14} className="animate-spin text-[#1f3e80]" />}
            </div>
          ) : (
            <Popover open={studyOpen} onOpenChange={setStudyOpen}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" role="combobox"
                  data-testid="upcoming-study-selector"
                  className="w-full justify-between rounded-sm bg-white font-normal text-stone-500">
                  <span className="flex items-center gap-2"><MagnifyingGlass size={15} /> {L.searchStudy}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
                <Command shouldFilter={false}>
                  <CommandInput placeholder={L.searchStudy} value={studyQuery} onValueChange={setStudyQuery}
                    data-testid="upcoming-study-search-input" />
                  <CommandList>
                    {studyQuery.trim().length < 2 ? (
                      <div className="py-6 text-center text-xs text-stone-400">{L.typeToSearch}</div>
                    ) : studySearching ? (
                      <div className="py-6 text-center text-xs text-stone-400">…</div>
                    ) : studyResults.length === 0 ? (
                      <CommandEmpty>{L.noStudies}</CommandEmpty>
                    ) : (
                      studyResults.map((s) => (
                        <CommandItem key={s.study_id} value={s.study_id} onSelect={() => selectStudy(s)}
                          data-testid={`upcoming-study-option-${s.study_id}`} className="cursor-pointer">
                          <div className="flex flex-col min-w-0">
                            <span className="truncate font-medium text-stone-800">{s.title || '(sin título)'}</span>
                            {s.code && <span className="text-xs text-stone-400">{s.code}</span>}
                          </div>
                        </CommandItem>
                      ))
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>

        {fieldText(L.name, 'name', { input: true })}
        {fieldText(L.executiveSummary, 'executive_summary', { rows: 4 })}
        {fieldText(L.inclusion, 'inclusion_criteria', { rows: 3 })}
        {fieldText(L.exclusion, 'exclusion_criteria', { rows: 3 })}
        {fieldText(L.withdrawal, 'withdrawal_criteria', { optional: true, rows: 2 })}
        {fieldText(L.duration, 'duration_visits', { rows: 2 })}
        {fieldText(L.compensation, 'compensation', { rows: 2 })}
        {fieldText(L.visit_times, 'visit_times', { rows: 2 })}

        {/* Audiencia (solo afecta a la App; la web publica es para todos) */}
        <div className="rounded-sm border border-stone-200 bg-stone-50/60 p-3 space-y-2" data-testid="upcoming-audience">
          <Label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
            <Users size={15} /> {L.audience}
          </Label>
          <p className="text-xs text-stone-500">{L.audienceHint}</p>
          <RadioGroup
            value={form.audience_type}
            onValueChange={(v) => setForm((f) => ({ ...f, audience_type: v, audience_segment_id: v === 'all' ? '' : f.audience_segment_id }))}
            className="gap-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="all" id="aud-all" data-testid="audience-all" />
              <Label htmlFor="aud-all" className="text-sm font-normal cursor-pointer">{L.audienceAll}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="segment" id="aud-seg" data-testid="audience-segment" />
              <Label htmlFor="aud-seg" className="text-sm font-normal cursor-pointer">{L.audienceSegment}</Label>
            </div>
          </RadioGroup>
          {form.audience_type === 'segment' && (
            segments.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">{L.audienceNoSegments}</p>
            ) : (
              <Select value={form.audience_segment_id || undefined}
                onValueChange={(v) => setForm((f) => ({ ...f, audience_segment_id: v }))}>
                <SelectTrigger className="bg-white" data-testid="audience-segment-select">
                  <SelectValue placeholder={L.audiencePickSegment} />
                </SelectTrigger>
                <SelectContent>
                  {segments.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`audience-segment-option-${s.id}`}>
                      {s.name}{segmentCounts[s.id] != null ? ` · ${segmentCounts[s.id]} ${es ? 'voluntarios' : 'volunteers'}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          )}
          {form.audience_type === 'segment' && form.audience_segment_id && segmentCounts[form.audience_segment_id] != null && (
            <p className="text-xs text-stone-500" data-testid="audience-segment-count">
              <Users size={12} className="inline mr-1" />
              {segmentCounts[form.audience_segment_id]} {es ? 'voluntarios cumplen este segmento ahora mismo' : 'volunteers currently match this segment'}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button onClick={save} disabled={saving} data-testid="upcoming-save-btn" className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
            {editingId ? <FloppyDisk size={16} /> : <Plus size={16} />}
            {saving ? '...' : (editingId ? L.update : L.create)}
          </Button>
        </div>
      </div>

      {/* Lista */}
      <div className="rounded-sm border border-stone-200 bg-white p-5">
        <h3 className="text-lg font-heading font-semibold text-stone-900 border-b border-stone-100 pb-3 mb-3">
          {L.list} <span className="text-sm font-normal text-stone-400">({items.length})</span>
        </h3>
        {loading ? (
          <p className="text-sm text-stone-400 py-6 text-center">...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-stone-400 py-6 text-center" data-testid="upcoming-empty">{L.empty}</p>
        ) : (
          <div className="space-y-3 max-h-[640px] overflow-y-auto pr-1">
            {items.map((it) => (
              <div key={it.id} data-testid={`upcoming-item-${it.id}`}
                className={`rounded-sm border p-3 ${it.published ? 'border-emerald-200 bg-emerald-50/40' : 'border-stone-200'}`}>
                <div className="flex items-start gap-2 min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-stone-900 break-words">{it.name}</span>
                      <Badge variant="outline" className={it.published
                        ? 'border-emerald-300 text-emerald-700 bg-white'
                        : 'border-stone-300 text-stone-500'}>
                        {it.published ? L.published : L.draft}
                      </Badge>
                      {it.web_sync_status === 'synced' ? (
                        <Badge variant="outline" className="border-sky-300 text-sky-700 bg-white gap-1 text-[11px]">
                          <CheckCircle size={12} weight="fill" /> {L.webSynced}
                        </Badge>
                      ) : it.web_sync_status === 'error' ? (
                        <Badge variant="outline" className="border-red-300 text-red-700 bg-white gap-1 text-[11px]" title={it.web_sync_error || ''}>
                          <Warning size={12} weight="fill" /> {L.webError}
                        </Badge>
                      ) : it.web_sync_status === 'pending' ? (
                        <Badge variant="outline" className="border-amber-300 text-amber-700 bg-white gap-1 text-[11px]">
                          {L.webPending}
                        </Badge>
                      ) : null}
                      {it.audience_type === 'segment' && (
                        <Badge variant="outline" className="border-violet-300 text-violet-700 bg-white gap-1 text-[11px]" data-testid={`upcoming-audience-badge-${it.id}`}>
                          <Users size={12} /> {(segments.find((s) => s.id === it.audience_segment_id) || {}).name || L.audienceSegment}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-stone-500 mt-1 line-clamp-2">{it.duration_visits} · {it.compensation}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-stone-500 hover:text-[#1f3e80]"
                      onClick={() => startEdit(it)} data-testid={`upcoming-edit-${it.id}`}>
                      <PencilSimple size={15} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-stone-500 hover:text-red-600"
                      onClick={() => remove(it)} data-testid={`upcoming-delete-${it.id}`}>
                      <Trash size={15} />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-stone-100">
                  <Switch checked={!!it.published} onCheckedChange={() => togglePublish(it)}
                    data-testid={`upcoming-publish-${it.id}`} />
                  <span className="text-xs font-medium text-stone-600">{L.publish}</span>
                  <Button type="button" size="sm" variant="ghost"
                    disabled={resendingWebId === it.id}
                    onClick={() => resendWeb(it)}
                    data-testid={`upcoming-resend-web-${it.id}`}
                    className="h-6 px-2 text-[11px] text-sky-700 hover:bg-sky-50 gap-1">
                    <ArrowClockwise size={12} className={resendingWebId === it.id ? 'animate-spin' : ''} />
                    {L.webResend}
                  </Button>
                  <button type="button" onClick={() => openInterests(it)}
                    data-testid={`upcoming-interests-${it.id}`}
                    className={`ml-auto flex items-center gap-1.5 text-xs rounded-sm px-2 py-1 border transition-colors ${
                      (it.interest_count || 0) > 0
                        ? 'border-[#1f3e80]/30 text-[#1f3e80] hover:bg-[#1f3e80]/5'
                        : 'border-stone-200 text-stone-400 hover:bg-stone-50'}`}>
                    <Users size={14} weight="duotone" />
                    <span className="font-semibold">{it.interest_count || 0}</span>
                    <span>{L.interested}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialogo de voluntarios interesados */}
      <Dialog open={!!interestsFor} onOpenChange={(o) => { if (!o) { setInterestsFor(null); setInterestRows([]); } }}>
        <DialogContent data-testid="upcoming-interests-dialog" className="max-w-lg overflow-hidden">
          <DialogHeader className="min-w-0">
            <DialogTitle className="flex items-center gap-2 min-w-0">
              <Users size={18} weight="duotone" className="text-[#1f3e80] flex-shrink-0" />
              <span className="truncate">{L.interestedTitle}{interestsFor ? ` · ${interestsFor.name}` : ''}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="min-w-0 max-h-[60vh] overflow-y-auto">
            {interestsLoading ? (
              <p className="text-sm text-stone-400 py-6 text-center">...</p>
            ) : interestRows.length === 0 ? (
              <p className="text-sm text-stone-400 py-6 text-center" data-testid="upcoming-interests-empty">{L.noInterest}</p>
            ) : (
              <ul className="divide-y divide-stone-100">
                {interestRows.map((r) => (
                  <li key={r.id} className="py-2.5 min-w-0" data-testid={`upcoming-interest-row-${r.volunteer_id}`}>
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <span className="font-medium text-sm text-stone-900 truncate">{r.volunteer_name || r.volunteer_id}</span>
                      <span className="text-xs text-stone-400 flex-shrink-0">{(r.created_at || '').slice(0, 10)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-0.5 text-xs text-stone-600">
                      {r.volunteer_email && (
                        <a href={`mailto:${r.volunteer_email}`} className="flex items-center gap-1 hover:text-[#1f3e80] truncate">
                          <Envelope size={13} /> {r.volunteer_email}
                        </a>
                      )}
                      {r.volunteer_phone && (
                        <a href={`tel:${r.volunteer_phone}`} className="flex items-center gap-1 hover:text-[#1f3e80]">
                          <Phone size={13} /> {r.volunteer_phone}
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1" data-testid={`upcoming-interest-crm-${r.volunteer_id}`}>
                      {r.crm_status === 'synced' ? (
                        <Badge variant="outline" className="border-emerald-300 text-emerald-700 gap-1 text-[11px]">
                          <CheckCircle size={12} weight="fill" /> {L.crmSynced}
                        </Badge>
                      ) : r.crm_status === 'error' ? (
                        <Badge variant="outline" className="border-red-300 text-red-700 gap-1 text-[11px]" title={r.crm_error || ''}>
                          <Warning size={12} weight="fill" /> {L.crmError}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-amber-300 text-amber-700 gap-1 text-[11px]">
                          {L.crmPending}
                        </Badge>
                      )}
                      {r.crm_status !== 'synced' && (
                        <Button type="button" size="sm" variant="ghost"
                          disabled={resendingId === r.id}
                          onClick={() => resendCrm(r)}
                          data-testid={`upcoming-interest-resend-${r.volunteer_id}`}
                          className="h-6 px-2 text-[11px] text-[#1f3e80] hover:bg-[#1f3e80]/10 gap-1">
                          <ArrowClockwise size={12} className={resendingId === r.id ? 'animate-spin' : ''} />
                          {L.crmResend}
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
